<template>
  <v-card class="content pa-0 ma-0 align-start" flat>
    <v-toolbar flat color="secondary">
      <v-toolbar-title class="white--text">License Manager</v-toolbar-title>
    </v-toolbar>
    <v-container class="pa-4">
      <v-row v-if="!licenseActive" class="px-3 pt-1 pb-4">
        <v-img
          v-for="(item, i) in labDetails"
          :key="i"
          :src="item.icon"
          max-width="32"
          max-height="32"
          class="mr-4"
        >
        </v-img>
      </v-row>
      <v-container class="text-h5 font-weight-bold">
        Welcome, {{displayName}}!
      </v-container>
      <v-container v-if="licenseActive" class="pa-0 ma-0">
        <v-container>
          Your BeyondLabz license is active!
          You will have access to the labs for
          <span class="font-weight-bold text-decoration-underline">{{daysLeft}}</span>
          more days.
        </v-container>
        <v-container class="pt-6">You are licensed to use the following products:</v-container>
        <v-container v-for="(item, i) in activeLabs" :key="i" class="row py-2">
          <v-img :src="item.icon" max-width="32" max-height="32" class="mr-3"></v-img>
          <span class="text-body-1 font-weight-bold mt-1">{{item.name}}</span>
        </v-container>
        <v-container class="pt-8">License Details:</v-container>
        <v-container>
          <v-text-field
            label="License Code"
            type="text"
            placeholder=""
            hide-details="true"
            outlined
            class="pb-4"
            :value="licenseCode"
            readonly
            color="primary"
          ></v-text-field>
          <v-text-field
            label="Expiration Date"
            type="text"
            placeholder=""
            hide-details="true"
            outlined
            class="pb-4"
            :value="licenseExpiration"
            readonly
            color="primary"
          ></v-text-field>
        </v-container>
        <v-container class="text-caption grey--text text--darken-2 pt-0 mt-n2">
          This license is only valid on this machine with your login.
        </v-container>
      </v-container>
      <v-container v-else>
        <v-container>
          Thank you for registering and logging into Beyond Labz!
          In order to run the virtual labs you need to have an active software license.
          If you received an activation code from your instructor or school please enter it below.
        </v-container>
        <v-btn
          text
          small
          color="primary"
          elevation="0"
          class="text-none mb-0 pb-0 ml-n3"
          @click="toggleLicenseTransfer"
        >Need help?</v-btn>
        <v-dialog flat outlined v-model="showLicenseTransfer" max-width="600">
          <v-card flat class="ma-0 pa-4">
            <v-card-title class="pb-6 justify-center">Activate Your License</v-card-title>
            <p>1. If you have been told to purchase a code, click on the Purchase button below.</p>
            <p>2. If you have been given an activation code by your instructor, enter it in the
              Activation box below.</p>
            <p>3. If you have already installed an older version of Beyond Labz, you can use the
              license from that version to activate this version as well. (a) Make sure the Beyond
              Labz Connector is not running. (b) Start v1.7, go to the License tab, and copy the
              license. (c) Quit v1.7. (d) In the License Manager, put in the copied license into the
              Activation box below.</p>
          </v-card>
        </v-dialog>
        <v-text-field
          v-model="redemptionCode"
          label="Activation Code"
          name="activationCode"
          type="text"
          outlined
          :error="redemptionFailed"
          :error-messages="errorMessages"
          class="pt-6 ma-0"
        ></v-text-field>

        <v-btn
          color="primary"
          elevation="0"
          @click="redeemCode"
        >
          Activate Code
        </v-btn>

        <v-container class="py-6">
          If you do not have an activation code, you can purchase a one year license to all five of
          the virtual labs.
        </v-container>

        <v-row class="px-3">
          <v-btn
            color="primary"
            outlined
            @click="purchaseLicense">
            Purchase License
          </v-btn>
          <v-spacer></v-spacer>
          <small>
            <v-btn
              text
              small
              color="primary"
              elevation="0"
              @click="toggleRequirements"
            >
              System Requirements
              <v-icon>{{requirementsIcon}}</v-icon>
            </v-btn>
          </small>

        </v-row>
        <v-expand-transition>
          <v-card
            v-show="showRequirements"
            flat
            class="mt-6"
          >
            <small class="text-center">
              <div class="my-2 text--secondary">
                Mac: MacOS Yosemite or later, 4GB Ram, 3GB Free disk space
              </div>
              <div class="my-2 text--secondary">
                Windows: Windows 8.0 or later (needs to be 64 bit), 4GB Ram, 3GB Free disk space
              </div>
              <div class="my-2 text--secondary">
                Note: Beyond Labz installation requires that you have administrator privileges for
                your workstation. You must have an internet connection to activate a license and
                install. We recommend that you have all system updates completed before you install
                Beyond Labz.
              </div>
            </small>
          </v-card>
        </v-expand-transition>
      </v-container>
    </v-container>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'License',
  data: () => ({
    redemptionFailed: false,
    showRequirements: false,
    showLicenseTransfer: false,
    errorMessages: [],
    redemptionCode: '',
    // TODO: point the bellow properties to PortalStateObject when data is available.
  }),
  computed: {
    ...mapGetters(['labs', 'authorizations', 'user', 'licenseActive', 'licenseDetails']),
    licenseCode() {
      return this.licenseDetails.license;
    },
    licenseExpiration() {
      const date = new Date(this.licenseDetails.end_date);
      return date.toLocaleString();
    },
    daysLeft() {
      const dateExpire = new Date(this.licenseDetails.end_date);
      const dateNow = Date.now();
      const timeLeft = dateExpire - dateNow;
      const daysLeft = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
      return Math.max(daysLeft, 0);
    },
    activeLabs() {
      return this.$LabDetails.labs.filter((item) => this.user.authorizations[item.short]);
    },
    labDetails() {
      return this.$LabDetails.labs;
    },
    requirementsIcon() {
      return this.showRequirements ? 'mdi-chevron-up' : 'mdi-chevron-down';
    },
    displayName() {
      return this.user.nickName || this.user.userName;
    },
  },
  methods: {
    redeemCode() {
      // const url = `https://portal.beyondlabz.com/payments/redeem/code/${this.redemptionCode}`;
      const url = 'https://portal.beyondlabz.com/api/portal/user/redeem';
      const data = {
        user_id: this.user.id,
        email_address: this.user.userName,
        code: this.redemptionCode,
      };
      console.log('License :: redeemCode :: post', url, data);
      this.$http.post(url, data).then((resp) => {
        console.log(resp);
        if (resp.status === 200) {
          if (resp.data.error === 1) {
            this.redemptionFailed = true;
            this.errorMessages = [resp.data.message];
          } else {
            this.user.license.push(resp.data.message);
          }
        } else {
          console.log('License :: redeemCode :: Failed', resp);
        }
      }).catch((error) => {
        console.log('License :: redeemCode :: Failed', error);
      });
    },
    purchaseLicense() {
      // TODO: hook into swipe integration
      console.log('Purchased license.');
      this.contents = [];
      this.showProgress = true;
      // Change this before going live ****************************************
      const url = 'https://portal.beyondlabz.com/payments/get_session/1/1/test';
      this.$http.post(url, { publicKey: 'pk_live_5uQcixx2WieNcp2Gb5fz5kNR00KhcnbZhY' })
        .then((resp) => {
          console.log(resp);
          if (resp.status === 200) {
            this.showProgress = false;
            this.contents = resp.data;
            this.redirectToStripe(resp.data);
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },
    redirectToStripe(data) {
      console.log('should be the session id but?', data);
      const stripe = window.Stripe('pk_live_5uQcixx2WieNcp2Gb5fz5kNR00KhcnbZhY');
      stripe.redirectToCheckout({ sessionId: data.session_id });
    },
    toggleRequirements() {
      this.showRequirements = !this.showRequirements;
    },
    toggleLicenseTransfer() {
      this.showLicenseTransfer = !this.showLicenseTransfer;
    },
  },
  watch: {
    redemptionCode() {
      this.redemptionFailed = false;
      this.errorMessages = [];
    },
  },
};
</script>

<style scoped>
.content {
  width: 100%;
  min-width: 360px;
  max-width: 640px;
}
.container {
  padding: 8px 0px 8px 0px;
  margin: 0;
}
</style>
